import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import {
  calculateRemainingResource,
  getDefaultValueCustomResource,
  getMergePackageName,
  getPackageName,
} from '@src/services/package';
import useUserInfo from '@src/hooks/useTtsUser';
import route from '@src/constants/route';
import { PACKAGE_TYPE } from '@src/constants/package';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import PackageContainer from './components/PackageContainer';
import PackageV2 from './PackageV2';

const SubscriptionCustomAndV2Item = ({
  packageInfo = {},
  usingOrder = {},
  totalRemainingCredits,
  isMobile,
  capturedReferralCreditsCustom,
}) => {
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { lockCharacters = 0 } = user;
  const { packageExpiryDate } = usingOrder;
  const { type, level } = packageInfo;
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser || {};
  const { oneTime } = studio || {};

  const isLockedCustom =
    usingOrder.type === PACKAGE_TYPE.STUDIO &&
    ttsUser?.studio?.custom?.remainingCredits &&
    ttsUser?.studio?.custom?.isLocked;

  const oneTimeExpiryDate = oneTime?.packageExpiryDate || packageExpiryDate;
  const hasOneTimeExpired =
    oneTimeExpiryDate && moment(oneTimeExpiryDate).isBefore(moment());
  const isLockedOneTimeCredit = hasOneTimeExpired && !!lockCharacters;

  const {
    defaultCredits = 0,
    defaultDevices = 0,
    defaultVoiceClonings = 0,
  } = getDefaultValueCustomResource(packageInfo?.customResource) || {};
  const { packages } = useMappingPackage();

  const handleRedirectToBuyPackage = () => {
    const currentPackage =
      packages?.find((p) => p.type === type && p.level === level) ||
      packageInfo;
    const redirectUrl = `${route.PAYMENT}?package=${currentPackage?.code}&credits=${defaultCredits}&devices=${defaultDevices}&voiceClonings=${defaultVoiceClonings}`;
    history.push(redirectUrl);
  };

  const remainingResource = calculateRemainingResource(
    type,
    user,
    hasOneTimeExpired,
  );

  const renderPackageButton = () => (
    <Button
      fullWidth
      className="buy-more-btn"
      onClick={handleRedirectToBuyPackage}
      data-id="button-buy"
      data-current-package-code={packageInfo?.code}
    >
      {t('buyNow')}
    </Button>
  );

  return (
    <>
      <PackageContainer
        packageName={getPackageName(
          packageInfo?.name,
          packageInfo?.code,
          language,
          t,
          true,
          true,
        )}
        productType={t(`${getMergePackageName(type)}`)}
        renderButton={renderPackageButton}
        packageDetail={
          <>
            <Box className="package-wrapper">
              <Typography className="title">{t('currentPlan')}</Typography>
              <PackageV2
                credits={totalRemainingCredits}
                packageExpiryDate={packageExpiryDate}
                isMobile={isMobile}
                isLocked={isLockedCustom}
                isCustomPackage
                referralCredits={capturedReferralCreditsCustom}
              />
            </Box>
            <Box className="package-wrapper">
              <Typography className="title">
                {`${t('oldPlan')} `}
                {oneTime?.packageCode && (
                  <span className="old-plan-package-name">
                    {`(${getPackageName(
                      null,
                      oneTime?.packageCode,
                      language,
                      t,
                      true,
                      true,
                    )})`}
                  </span>
                )}
              </Typography>
              <PackageV2
                isLocked={isLockedOneTimeCredit}
                credits={remainingResource}
                packageExpiryDate={oneTimeExpiryDate}
                isMobile={isMobile}
              />
            </Box>
          </>
        }
        isMobile={isMobile}
      />
    </>
  );
};

export default SubscriptionCustomAndV2Item;
